import React from "react";
import { Check, X, Phone, MapPin, Mail, Package, Link2, ScrollText, Landmark, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/lib/api";
import { STATUS, ORDER_STATUS_LABELS, ORDER_STATUS_STYLE, TIERS } from "@/pages/admin/customers/customerConstants";

// Full customer profile drawer: follow-up summary, purchase history, payments/outstanding,
// pricing tier, billing & tax details, CRM notes, and order history.
export const CustomerDetailDrawer = ({
  selected, onClose, purchases, staff,
  onUpdateStatus, onLinkLead, onOpenLedger, onLogContact, onDelete,
  tier, onTierChange, discountPct, setDiscountPct, onSaveDiscount,
  billing, setBilling, onUpdBillAddr, onUpdShipAddr, onSaveBilling,
  notes, setNotes, onSaveNotes,
}) => (
  <Drawer open={!!selected} onOpenChange={(o) => !o && onClose()}>
    <DrawerContent className="max-h-[85vh]">
      {selected && (
        <div className="max-w-2xl mx-auto w-full px-4 pb-8 overflow-y-auto">
          <DrawerHeader className="px-0"><DrawerTitle className="font-display text-2xl flex items-center gap-3 flex-wrap">{selected.shop_name} <Badge className={STATUS[selected.status]}>{selected.status}</Badge>
            <div className="ml-auto flex items-center gap-2">
              <Button data-testid="customer-view-ledger" size="sm" variant="outline" className="gap-1" onClick={() => onOpenLedger(selected)}><ScrollText className="h-3.5 w-3.5" /> Ledger</Button>
              <Button data-testid="customer-log-contact" size="sm" className="gap-1" onClick={onLogContact}><Phone className="h-3.5 w-3.5" /> Log Contact</Button>
            </div>
          </DrawerTitle></DrawerHeader>

          {selected.matched_lead_id && (
            <div className="rounded-lg border border-[#E7D0A8] bg-[#FBF1DF] p-3 mb-4 flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-[#7A4B12]">This phone matches lead &ldquo;<b>{selected.matched_lead_company}</b>&rdquo; &mdash; link it as a converted customer?</span>
              <Button data-testid="customer-link-lead-drawer" size="sm" variant="secondary" className="gap-1" onClick={() => onLinkLead(selected)}><Link2 className="h-3.5 w-3.5" /> Link &amp; Convert</Button>
            </div>
          )}

          <div className="rounded-lg border border-border bg-[#F7F0E6] p-4 mb-4" data-testid="customer-followup-summary">
            <div className="text-sm font-medium mb-2">Follow-Up Summary</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><div className="text-xs text-muted-foreground">Frequency</div>{selected.frequency_days || 7} days</div>
              <div><div className="text-xs text-muted-foreground">Last Contact</div>{selected.last_contact || "—"}</div>
              <div><div className="text-xs text-muted-foreground">Next Contact</div>{selected.next_contact || "—"}</div>
              <div><div className="text-xs text-muted-foreground">Salesman</div>{selected.salesman_name || "Unassigned"}</div>
            </div>
          </div>

          {purchases && (
            <div className="rounded-lg border border-border p-4 mb-4" data-testid="customer-purchase-history">
              <div className="flex items-center justify-between mb-2"><div className="text-sm font-medium">Purchase History</div><div className="text-xs text-muted-foreground">Total gold: <span className="font-mono">{purchases.total_gold_g} g</span> · {purchases.order_count || 0} orders</div></div>
              {(!purchases.orders || purchases.orders.length === 0) ? <p className="text-sm text-muted-foreground">No purchases yet.</p> : (
                <div className="space-y-1.5">
                  {purchases.orders.map((o, i) => (
                    <div key={o.order_no || i} className="flex items-center justify-between text-sm border-b border-border py-1.5">
                      <span className="font-mono">{o.order_no}</span>
                      <span className="text-muted-foreground text-xs">{o.date}</span>
                      <span className="text-xs">{o.gold_weight_g}g · {o.purity}</span>
                      <span className="font-mono">{formatINR(o.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <PaymentsOutstandingCard orders={selected.orders || []} />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" /> {selected.phone}</div>
            <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {selected.city || "-"}</div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {selected.email || "-"}</div>
          </div>
          <div className="flex gap-2 mt-4">
            {selected.status !== "approved" && <Button size="sm" onClick={() => onUpdateStatus(selected.id, "approved")} className="gap-1"><Check className="h-4 w-4" /> Approve</Button>}
            {selected.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => onUpdateStatus(selected.id, "rejected")} className="gap-1"><X className="h-4 w-4" /> Reject</Button>}
            {staff?.is_manager && (
              <Button data-testid="customer-delete-button-drawer" size="sm" variant="ghost" className="gap-1 text-destructive hover:text-destructive ml-auto" onClick={() => onDelete(selected)}><Trash2 className="h-4 w-4" /> Delete Customer</Button>
            )}
          </div>

          <div className="mt-5 rounded-lg border border-border bg-[#F7F0E6] p-4" data-testid="shop-pricing-tier-editor">
            <div className="text-sm font-medium mb-3">Diamond Discount Tier</div>
            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <Label className="text-xs">Tier</Label>
                <Select value={tier} onValueChange={onTierChange}>
                  <SelectTrigger data-testid="shop-tier-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(TIERS).map((t) => <SelectItem key={t} value={t}>{t} ({TIERS[t]}%)</SelectItem>)}
                    <SelectItem value="Custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Diamond Discount %</Label>
                <Input data-testid="shop-discount-input" type="number" value={discountPct} onChange={(e) => { setDiscountPct(e.target.value); onTierChange("Custom"); }} className="mt-1 font-mono" />
              </div>
            </div>
            <Button data-testid="shop-discount-save" size="sm" className="mt-3" onClick={onSaveDiscount}>Save Pricing Tier</Button>
            <p className="text-xs text-muted-foreground mt-2">This shop gets {discountPct >= 0 ? `${discountPct}% off` : `${Math.abs(discountPct)}% markup on`} the diamond/stone value only — across the catalog, cart and invoices. Gold weight, wastage and making charges are never discounted. Enter a negative number for a markup instead of a discount.</p>
          </div>

          <div className="mt-5 rounded-lg border border-border bg-[#F7F0E6] p-4" data-testid="shop-billing-editor">
            <div className="text-sm font-medium mb-3 flex items-center gap-2"><Landmark className="h-4 w-4" /> Billing &amp; Tax Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">GSTIN</Label>
                <Input data-testid="shop-gstin-input" value={billing.gstin} onChange={(e) => setBilling((b) => ({ ...b, gstin: e.target.value.toUpperCase() }))}
                  placeholder="e.g. 27ABCDE1234F1Z5" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs">State (for GST — CGST+SGST vs IGST)</Label>
                <Input data-testid="shop-state-input" value={billing.state} onChange={(e) => setBilling((b) => ({ ...b, state: e.target.value }))}
                  placeholder="e.g. Maharashtra" className="mt-1" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Billing Address</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                <Input data-testid="shop-billing-line1" value={billing.billing_address.line1} onChange={(e) => onUpdBillAddr("line1", e.target.value)} placeholder="Address line 1" />
                <Input value={billing.billing_address.line2} onChange={(e) => onUpdBillAddr("line2", e.target.value)} placeholder="Address line 2 (optional)" />
                <Input value={billing.billing_address.city} onChange={(e) => onUpdBillAddr("city", e.target.value)} placeholder="City" />
                <Input value={billing.billing_address.pincode} onChange={(e) => onUpdBillAddr("pincode", e.target.value)} placeholder="Pincode" />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Switch data-testid="shop-ship-same-toggle" checked={billing.ship_same_as_billing}
                onCheckedChange={(v) => setBilling((b) => ({ ...b, ship_same_as_billing: v }))} />
              <Label className="text-xs cursor-pointer" onClick={() => setBilling((b) => ({ ...b, ship_same_as_billing: !b.ship_same_as_billing }))}>Shipping address same as billing</Label>
            </div>
            {!billing.ship_same_as_billing && (
              <div className="mt-2">
                <Label className="text-xs">Shipping Address</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                  <Input data-testid="shop-shipping-line1" value={billing.shipping_address.line1} onChange={(e) => onUpdShipAddr("line1", e.target.value)} placeholder="Address line 1" />
                  <Input value={billing.shipping_address.line2} onChange={(e) => onUpdShipAddr("line2", e.target.value)} placeholder="Address line 2 (optional)" />
                  <Input value={billing.shipping_address.city} onChange={(e) => onUpdShipAddr("city", e.target.value)} placeholder="City" />
                  <Input value={billing.shipping_address.pincode} onChange={(e) => onUpdShipAddr("pincode", e.target.value)} placeholder="Pincode" />
                </div>
              </div>
            )}
            <div className="mt-3 max-w-[240px]">
              <Label className="text-xs">Payment Terms Override (days)</Label>
              <Input data-testid="shop-payment-terms-input" type="number" value={billing.payment_terms_days}
                onChange={(e) => setBilling((b) => ({ ...b, payment_terms_days: e.target.value }))}
                placeholder="Uses business default" className="mt-1 font-mono" />
            </div>
            <Button data-testid="shop-billing-save" size="sm" className="mt-3" onClick={onSaveBilling}>Save Billing &amp; Tax Details</Button>
            <p className="text-xs text-muted-foreground mt-2">Used to auto-calculate CGST+SGST (same state) or IGST (different state) and the invoice due date on future invoices.</p>
          </div>
          <div className="mt-5">
            <div className="text-sm font-medium mb-2">CRM Notes</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Add notes about this customer..." />
            <Button size="sm" className="mt-2" onClick={onSaveNotes}>Save Notes</Button>
          </div>
          <div className="mt-6">
            <div className="text-sm font-medium mb-2 flex items-center gap-2"><Package className="h-4 w-4" /> Order History ({(selected.orders || []).length})</div>
            {(selected.orders || []).length === 0 ? <p className="text-sm text-muted-foreground">No orders yet.</p> : (
              <div className="space-y-2">{selected.orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-sm border-b border-border py-2"><span className="font-mono">{o.order_no}</span><Badge className={ORDER_STATUS_STYLE[o.status] || ""}>{ORDER_STATUS_LABELS[o.status] || o.status}</Badge><span className="font-mono">{formatINR(o.total)}</span></div>
              ))}</div>
            )}
          </div>
        </div>
      )}
    </DrawerContent>
  </Drawer>
);

// Aggregated payments + outstanding balance across all of this customer's orders.
function PaymentsOutstandingCard({ orders }) {
  const totalOutstanding = orders.reduce((s, o) => s + (o.outstanding_amount || 0), 0);
  const totalPaid = orders.reduce((s, o) => s + (o.amount_paid || 0), 0);
  const payments = orders.flatMap((o) => (o.payments || []).map((p) => ({ ...p, order_no: o.order_no })))
    .sort((a, b) => (b.recorded_at || "").localeCompare(a.recorded_at || ""));
  if (orders.length === 0) return null;
  return (
    <div className="rounded-lg border border-border p-4 mb-4" data-testid="customer-payments-outstanding">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-sm font-medium">Payments & Outstanding</div>
        <div className="flex items-center gap-2">
          <Badge className="bg-[#E7F3EA] text-[#1F5B2E] border border-[#BFDCC7]">Paid {formatINR(totalPaid)}</Badge>
          {totalOutstanding > 0 && <Badge data-testid="customer-outstanding-badge" className="bg-[#FBE7E7] text-[#7A1F1F] border border-[#E7B9B9]">Outstanding {formatINR(totalOutstanding)}</Badge>}
        </div>
      </div>
      {payments.length === 0 ? <p className="text-sm text-muted-foreground">No payments recorded yet.</p> : (
        <div className="space-y-1.5 max-h-40 overflow-y-auto">
          {payments.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm border-b border-border py-1.5">
              <span className="font-mono text-xs text-muted-foreground">{p.order_no}</span>
              <span className="text-xs capitalize">{(p.mode || "").replace("_", " ")}</span>
              <span className="text-xs text-muted-foreground">{(p.recorded_at || "").slice(0, 10)}</span>
              <span className="font-mono text-[#1F5B2E]">{formatINR(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
